import { useState } from 'react';
import { toast } from 'sonner';
import { FileText, X, Trash2, Loader2, CheckCircle2, Send } from 'lucide-react';
import { useRfq } from '../context/RfqContext';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

export default function RfqDrawer() {
  const { items, updateItem, removeItem, clear, open, setOpen } = useRfq();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', country: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error(t('rfq.addFirst'));
    setSending(true);
    try {
      await api.post('/quotes', { ...form, items });
      setDone(true);
      clear();
      setForm({ name: '', email: '', phone: '', company: '', country: '', message: '' });
    } catch {
      toast.error(t('rfq.failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="rfq-basket-button"
        onClick={() => { setDone(false); setOpen(true); }}
        className="fixed bottom-24 right-4 sm:right-6 z-40 flex items-center gap-2 bg-ink-light text-white rounded px-3 py-3 sm:pl-4 sm:pr-5 shadow-xl hover:bg-ink transition-colors"
      >
        <FileText className="w-4 h-4 shrink-0" />
        <span className="hidden sm:inline text-xs font-bold uppercase tracking-wider">{t('rfq.button')}</span>
        {items.length > 0 && (
          <span data-testid="rfq-count" className="ml-1 w-5 h-5 text-[11px] font-bold bg-blue-brand text-white flex items-center justify-center rounded-full">
            {items.length}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="rfq-drawer">
          <button aria-label="Close" onClick={() => setOpen(false)}
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-surface text-white px-6 py-5 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-blue-brand">{t('rfq.kicker')}</p>
                <h2 className="font-serif text-2xl mt-1">{t('rfq.heading')}</h2>
              </div>
              <button data-testid="rfq-close" onClick={() => setOpen(false)} className="p-2 text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {done ? (
              <div className="p-10 text-center" data-testid="rfq-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                <h3 className="font-heading text-lg font-bold uppercase text-ink mt-5">{t('rfq.sentTitle')}</h3>
                <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                  {t('rfq.sentText')}
                </p>
                <button onClick={() => setOpen(false)}
                        className="mt-7 bg-ink-light text-white text-xs font-bold uppercase tracking-wider px-6 py-3">
                  {t('rfq.continue')}
                </button>
              </div>
            ) : (
              <div className="p-6">
                {items.length === 0 ? (
                  <p className="text-sm text-gray-400 py-10 text-center" data-testid="rfq-empty">
                    {t('rfq.empty')}
                  </p>
                ) : (
                  <div className="space-y-4 mb-8" data-testid="rfq-items">
                    {items.map((item, i) => (
                      <div key={i} className="border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-heading text-sm font-bold uppercase text-ink">{item.name}</p>
                            {item.form && <p className="text-xs text-gray-400 mt-0.5">{item.form}</p>}
                          </div>
                          <button data-testid={`rfq-remove-${i}`} onClick={() => removeItem(i)}
                                  className="text-gray-300 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <input
                            data-testid={`rfq-size-${i}`}
                            value={item.size}
                            onChange={(e) => updateItem(i, { size: e.target.value })}
                            placeholder={t('rfq.size')}
                            className="border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:border-ink"
                          />
                          <input
                            data-testid={`rfq-qty-${i}`}
                            value={item.quantity}
                            onChange={(e) => updateItem(i, { quantity: e.target.value })}
                            placeholder={t('rfq.quantity')}
                            className="border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:border-ink"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input required data-testid="rfq-name" value={form.name} placeholder={t('rfq.name')}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           className="border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                    <input required type="email" data-testid="rfq-email" value={form.email} placeholder={t('rfq.email')}
                           onChange={(e) => setForm({ ...form, email: e.target.value })}
                           className="border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                    <input data-testid="rfq-company" value={form.company} placeholder={t('rfq.company')}
                           onChange={(e) => setForm({ ...form, company: e.target.value })}
                           className="border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                    <input data-testid="rfq-phone" value={form.phone} placeholder={t('rfq.phone')}
                           onChange={(e) => setForm({ ...form, phone: e.target.value })}
                           className="border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                  </div>
                  <input data-testid="rfq-country" value={form.country} placeholder={t('rfq.country')}
                         onChange={(e) => setForm({ ...form, country: e.target.value })}
                         className="w-full border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                  <textarea data-testid="rfq-message" value={form.message} rows={3}
                            placeholder={t('rfq.message')}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="w-full border border-gray-200 text-sm px-3 py-3 focus:outline-none focus:border-ink" />
                  <button
                    type="submit"
                    data-testid="rfq-submit"
                    disabled={sending}
                    className="w-full bg-ink-light text-white font-bold uppercase tracking-wider text-sm px-6 py-4 hover:bg-ink transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {t('rfq.submit')}
                  </button>
                  <p className="text-[11px] text-gray-400 text-center">
                    {t('rfq.note')}
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
